<script setup>
import { getCampaignData } from '../../../utilities/investment-process/useCampaign'
import BaseButtonMuted from '../../base/BaseButtonMuted.vue'
import { formatCurrency } from '../../../utilities/helpers'
import { storeToRefs } from 'pinia'
import { useInvestmentProcessDataState } from '../../../utilities/investment-process/useInvestmentProcess'
import { computed, ref, watch } from 'vue'
import { CheckCircleIcon } from '@heroicons/vue/outline'
const { investmentAmount, incentiveUid } = storeToRefs(useInvestmentProcessDataState())

const incentives = getCampaignData().assets_incentives

const sortedIncentives = computed(() => {
    return [...incentives].sort((a, b) => {
        return a.incentives_amount - b.incentives_amount
    })
})

function setInvestmentAmount(amount) {
    investmentAmount.value = amount
}

const currentIncentive = computed(() => {
    return getCampaignData().assets_incentives.find((incentive) => incentive.asset_inc_uid === incentiveUid.value)
})

function isIncentiveAvaliable(incentive) {
    return investmentAmount.value >= incentive?.incentives_amount
}

function isSelectedIncentive(incentive) {
    return currentIncentive.value?.asset_inc_uid === incentive?.asset_inc_uid
}

function setIncentive(assetIncentive) {
    incentiveUid.value = assetIncentive.asset_inc_uid
    setInvestmentAmount(assetIncentive.incentives_amount)
}

watch(
    () => investmentAmount.value,
    () => {
        // set highest possible incentive
        const newIncentive = [...sortedIncentives.value].reverse().find((searchIncentive) => {
            return isIncentiveAvaliable(searchIncentive)
        })
        if (newIncentive) {
            incentiveUid.value = newIncentive.asset_inc_uid
        } else {
            incentiveUid.value = null
        }
    }
)
</script>

<template>
    <p class="py-3 text-base font-bold text-gray-700">{{ $t('investment_process.incentives') }}</p>

    <ul
        role="list"
        class="ml-0 divide-y divide-gray-100 overflow-hidden bg-white pl-0 shadow-sm ring-1 ring-gray-900/5 sm:rounded-xl">
        <li
            v-for="assetIncentive in sortedIncentives"
            :key="assetIncentive.asset_inc_uid"
            class="relative flex cursor-pointer justify-between gap-x-6 px-4 py-5 hover:bg-gray-50 sm:px-6"
            @click="setIncentive(assetIncentive)">
            <div class="flex w-3/4 items-center gap-x-4">
                <div class="min-w-0 flex-auto">
                    <p class="text-sm font-semibold leading-6 text-gray-900">
                        {{ assetIncentive.incentives_title }} -
                        <span class="text-primary">{{
                            formatCurrency(
                                assetIncentive.incentives_amount,
                                getCampaignData().asset_data.currency_string
                            )
                        }}</span>
                    </p>
                    <div
                        class="mt-1 flex text-xs leading-5 text-gray-500"
                        v-html="assetIncentive.incentives_description"></div>
                </div>
            </div>
            <div class="flex w-1/4 items-center justify-center gap-x-4">
                <div class="sm:flex sm:flex-col sm:items-end">
                    <BaseButtonMuted
                        :disabled="isSelectedIncentive(assetIncentive)"
                        @click="setIncentive(assetIncentive)">
                        <div
                            v-if="isSelectedIncentive(assetIncentive)"
                            class="flex w-full items-center justify-center space-x-1.5">
                            <CheckCircleIcon class="h-5 w-5 text-green-600"></CheckCircleIcon>
                            <span class="text-sm text-gray-700">{{ $t('investment_process.selected_incentive') }}</span>
                        </div>
                        <div v-else>{{ $t('investment_process.select_incentive') }}</div>
                    </BaseButtonMuted>
                </div>
            </div>
        </li>
    </ul>
</template>

<style scoped></style>
